import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Chip } from './Button';
import { SearchUserCard } from './UserCards';

const StripContainer = styled.div`
  background: ${({ theme }) => theme.colors.bg2};
  border-radius: 20px;
  padding: 16px;
  margin: 0 auto 20px;
  width: calc(100% - 24px);
  max-width: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 8px 32px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    border-radius: 16px;
    margin-bottom: 16px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 0 12px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CtaButton = styled(Chip).attrs({ $size: 'small' })`
  flex-shrink: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  border-color: ${({ theme }) => theme.colors.primary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;
  }
`;

const ScrollRow = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0 2px 4px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slot = styled.div`
  flex-shrink: 0;
  width: 200px;
`;

const LOAD_MORE_THRESHOLD_PX = 240;

/**
 * Horizontal strip of creators.
 * - fetchCreators(): one-shot list.
 * - cta: optional { label, onClick } button shown at the right of the title.
 * - fetchPage(page): paged list; the next page loads when the row is scrolled near its right end.
 *   A page shorter than pageSize means there is nothing more to load.
 */
const CreatorStrip = ({ title, badge, icon, fetchCreators, fetchPage, pageSize = 10, cta }) => {
  const [creators, setCreators] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const loadingRef = useRef(false);
  const nextPageRef = useRef(0);

  const loadPage = useCallback(async () => {
    if (!fetchPage || loadingRef.current) return;
    loadingRef.current = true;
    try {
      const data = await fetchPage(nextPageRef.current);
      const items = Array.isArray(data) ? data : [];
      nextPageRef.current += 1;
      setCreators(prev => {
        const seen = new Set(prev.map(c => c.id));
        return [...prev, ...items.filter(c => !seen.has(c.id))];
      });
      setHasMore(items.length >= pageSize);
    } catch (error) {
      setHasMore(false);
    } finally {
      loadingRef.current = false;
    }
  }, [fetchPage, pageSize]);

  useEffect(() => {
    let mounted = true;
    if (fetchPage) {
      nextPageRef.current = 0;
      loadingRef.current = false;
      setCreators([]);
      setHasMore(false);
      loadPage();
    } else if (fetchCreators) {
      fetchCreators().then(data => {
        if (mounted) setCreators(data);
      });
    }
    return () => { mounted = false; };
  }, [fetchCreators, fetchPage, loadPage]);

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollLeft, clientWidth, scrollWidth } = e.currentTarget;
    if (scrollLeft + clientWidth >= scrollWidth - LOAD_MORE_THRESHOLD_PX) {
      loadPage();
    }
  };

  if (creators.length === 0) return null;

  return (
    <StripContainer>
      <TitleRow>
        <Title>{title}</Title>
        {cta && <CtaButton type="button" onClick={cta.onClick}>{cta.label}</CtaButton>}
      </TitleRow>
      <ScrollRow onScroll={handleScroll}>
        {creators.map(creator => (
          <Slot key={creator.id}>
            <SearchUserCard user={creator} avatarSize="44px" />
          </Slot>
        ))}
      </ScrollRow>
    </StripContainer>
  );
};

export default CreatorStrip;
